import React, { useState, useRef, useCallback, useEffect } from 'react';
// コンポーネントのインポート
import Map from './components/Map';
import Menu from './components/Menu';
import Notification from './components/Notification';
import ResearchHere from './components/ResearchHere';
import LocationInfo from './components/LocationInfo';
import LayerPopup from './components/LayerPopup';
import PrintPopup from './components/PrintPopup';
import PrintMapNormal from './components/PrintMapNormal';
import PrintMapSuper from './components/PrintMapSuper';
import DesktopAd from './components/DesktopAd';
import MobileAd from './components/MobileAd';
import ViewInApp from './components/ViewInApp';
import naviSearchModel from './components/navi/naviSearchModel';
import KeyboardInfo from './components/KeyboardInfo';
// Kntr用
import KntrMap from './components/KntrMap';
import KntrLocationInfo from './components/KntrLocationInfo';

// サービスのインポート
import { executeSearch } from './services/searchService';
import { updateLocationInfo } from './services/locationInfoService';
import { fetchWeather } from './services/weatherInfoService';
import { toggleMapContentFullScreen } from './services/fullScreenService';
import { takeMapSnapshot } from './services/snapshotService';
import { getCurrentLocation, getCurrentLocationCoords } from './services/currentLocationService';
import { resetMapToNorth } from './services/mapService';
import { resetMapToDefault } from './services/mapService';
import { getLonLat } from './services/mapCodeService';
// フックのインポート
import { useUrlSync, parseRoute, removePoiParam } from './hooks/useUrlSync';
// ユーティリティ関数のインポート
import { isMobileUa } from './utils/detectDevice';
import { sanitizeKeyword } from './utils/sanitizeKeyword';
// その他のインポート
import config from './config/config';
import './App.css';

/**
 * 定数定義
 */
const POSITION_THRESHOLD = Number(config.map.thresholds.position) || 0.0001;
const ZOOM_THRESHOLD = Number(config.map.thresholds.zoom) || 0.01;
const DEFAULT_STYLE_NAME = config.map.defaultStyleName;
const PLACE_PREFIX = config.search.placePrefix;
const MAX_KEYWORD_LENGTH = config.search.maxKeywordLength;
const SEARCH_QUERY_PARAM = config.search.queryParam;
const MYSPOT_PARAM = config.search.mySpotParam;
const HISTORY_PARAM = config.search.historyParam;
const SEARCH_POI_PARAM = config.search.poiParam;
const KNTR_PARAM_KEY = config.map.kntrParamKey;
const KNTR_PARAM_VALUE = config.map.kntrParamValue;
const STYLES = config.map.styles;
const PITCH_FOR_3D_MODE = config.map.pitchFor3dMode;

const ADDRESS_SEARCHING = config.locationInfo.messages.addressSearching;
const ADDRESS_NOT_FOUND = config.locationInfo.messages.addressNotFound;
const ADDRESS_FETCH_FAILED = config.locationInfo.messages.addressFetchFailed;

const MAPCODE_EMPTY = config.map.mapcode.messages.empty;
const MAPCODE_PARSE_ERROR = config.map.mapcode.messages.parseError;
const MAPCODE_CONVERSION_ERROR = config.map.mapcode.messages.conversionError;

const SEARCH_NO_RESULTS = config.search.messages.noResults;
const NOTIFICATION_HIDE_DELAY_MS = config.search.notificationHideDelayMs;
const CATEGORY_TYPE_STATION = config.search.station.categoryTypeStation;
const CATEGORY_TYPE_STATION_JP = config.search.station.categoryTypeStationJp;

const KEY_NAME_ESCAPE = config.common.keyNames.escape;
const KEY_NAME_SLASH = config.common.keyNames.slash;

/**
 * コンポーネント定義
 */
function App() {
  // ==============================
  // 1. フック（useStateなど）
  // ==============================

  // ------------------------------
  // 1.1 デバイス判定
  // ------------------------------
  // モバイル・タブレットサイズ判定用（画面サイズのレスポンシブ対応）
  const TABLET_BREAKPOINT = Number(config.common.tabletBreakpoint);
  const [isTablet, setIsTablet] = useState(() => window.innerWidth <= TABLET_BREAKPOINT);

  // 端末種別(UA)で初回に確定(リサイズでは切り替えない)
  // 広告配信タイプと「アプリで見る」の表示制御に使用する
  const [isMobileDevice] = useState(() => isMobileUa());

  useEffect(() => {
    // タブレット判定用（モバイルを兼ねる）のメディアクエリ。画面幅により表示を変更する制御のため
    const mq = window.matchMedia(`(max-width: ${TABLET_BREAKPOINT}px)`);
    const onChange = (e) => setIsTablet(e.matches);

    // 初期状態を即反映
    setIsTablet(mq.matches);

    // 互換対応: MediaQueryList のイベントリスナーAPI差分に対応
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    if (typeof mq.addListener === 'function') {
      mq.addListener(onChange);
      return () => mq.removeListener(onChange);
    }

    // 最終フォールバック：resize
    const handleResize = () => setIsTablet(window.innerWidth <= TABLET_BREAKPOINT);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [TABLET_BREAKPOINT]);

  // ------------------------------
  // 1.1.1 起動モード（URLで初期確定）
  // ------------------------------
  const [isFromKntr] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get(KNTR_PARAM_KEY) === KNTR_PARAM_VALUE;
  });

  // ------------------------------
  // 1.2 マップ関連状態
  // ------------------------------
  const mapInstance = useRef(null); // 地図インスタンス保持

  // parseRoute で URL を解析して初期 mapConfig を取得
  const [mapConfig, setMapConfig] = useState(() => {
    const routeData = parseRoute(window.location.pathname || '', {
      isMobileDevice,
      search: window.location.search,
    });

    // kntrはプレーン固定
    if (isFromKntr) {
      const plainStyleUrl = config.api.map.styles(DEFAULT_STYLE_NAME);
      return {
        ...routeData.mapConfig,
        style: plainStyleUrl,
        styleName: DEFAULT_STYLE_NAME,
      };
    }
    return routeData.mapConfig;
  });

  const [isMapInitialized, setIsMapInitialized] = useState(false); // 地図初期化済みフラグ
  const [is3dMode, setIs3dMode] = useState(false); // 3DモードON/OFF
  const [isCenterIndicatorEnabled, setIsCenterIndicatorEnabled] = useState(true); // 中心点インジケーター表示

  // ------------------------------
  // 1.3 UI状態
  // ------------------------------
  const [isMenuOpen, setIsMenuOpen] = useState(false); // メニューの開閉状態
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false); // 検索モーダルの表示状態
  const [isCategorySearchModalOpen, setIsCategorySearchModalOpen] = useState(false); // カテゴリ検索モーダルの表示状態
  const [isSearchResultOpen, setIsSearchResultOpen] = useState(false); // 検索結果パネル表示状態
  const [isSearchResultVisible, setIsSearchResultVisible] = useState(true); // 検索結果パネルの一時表示状態（falseで閉じずに折りたたむ）
  const [isLayerPopupOpen, setIsLayerPopupOpen] = useState(false); // レイヤーポップアップ表示状態
  const [isPrintPopupOpen, setIsPrintPopupOpen] = useState(false); // 印刷ポップアップ表示状態
  const [isPrintMapNormalOpen, setIsPrintMapNormalOpen] = useState(false); // 通常印刷モーダル表示
  const [isPrintMapSuperOpen, setIsPrintMapSuperOpen] = useState(false); // 超印刷モーダル表示
  const [notification, setNotification] = useState({ open: false, message: '' }); // 通知表示
  const [isKeyboardInfoOpen, setIsKeyboardInfoOpen] = useState(false); // キーボードショートカット情報表示状態

  // 検索系モーダル開閉ハンドラ
  const onOpenSearchModal = useCallback(() => setIsSearchModalOpen(true), []);
  const onCloseSearchModal = useCallback(() => setIsSearchModalOpen(false), []);
  const onOpenCategorySearchModal = useCallback(() => setIsCategorySearchModalOpen(true), []);
  const onCloseCategorySearchModal = useCallback(() => setIsCategorySearchModalOpen(false), []);

  // navi関連
  const [isShowNavi, setIsShowNavi] = useState(false);
  const [naviSearch, setNaviSearch] = useState(naviSearchModel);

  // ------------------------------
  // 1.4 検索関連状態
  // ------------------------------
  const [searchResult, setSearchResult] = useState(null); // 検索結果データ
  const [searchQuery, setSearchQuery] = useState(''); // 検索クエリ（検索ボックスの値）
  const [selectedIndex, setSelectedIndex] = useState(null); // 検索結果の選択インデックス
  const [lastSearchTerm, setLastSearchTerm] = useState(''); // 最後に検索したワード
  const [hasMapMoved, setHasMapMoved] = useState(false); // 地図移動フラグ（再検索に使用）
  const [hoveredIndex, setHoveredIndex] = useState(null);  // 検索結果のマウスオーバー中のインデックス
  const [isResearchHereEnabled, setIsResearchHereEnabled] = useState(true); // 「この辺で検索」ボタンの無効化状態
  const [selectedCategory, setSelectedCategory] = useState(null); // 選択中のカテゴリ
  const [isPoiSearchNoResults, setIsPoiSearchNoResults] = useState(false); // POI検索で結果0件だったかどうか

  // ------------------------------
  // 1.5 データ状態
  // ------------------------------
  // 住所情報
  const [locationInfo, setLocationInfo] = useState({
    address: ADDRESS_SEARCHING,
    stations: [],
    center: null
  });
  const [weather, setWeather] = useState(null); // 天気情報
  const [currentLocationButtonState, setCurrentLocationButtonState] = useState('inactive'); // inactive:初期状態、locating:測位中、located:測位完了

  // ------------------------------
  // 1.6 長押し関連状態
  // ------------------------------
  const [isLongPressPopupOpen, setIsLongPressPopupOpen] = useState(false); // 長押しポップアップ表示
  const [longPressPosition, setLongPressPosition] = useState(null); // 長押し位置情報

  // ==============================
  // 2.ユーティリティ関数
  // ==============================

  // 長押しポップアップを、LocationInfoのボタンから呼ぶための関数（地図中心で開く）
  const openLongPressAtMapCenter = useCallback(() => {
    const map = mapInstance.current;
    if (!map) return;
    const center = map.getCenter();
    setLongPressPosition({ lat: center.lat, lng: center.lng });
    setIsLongPressPopupOpen(true);
  }, []);

  // 通知表示関数
  const showNotification = useCallback((message) => {
    setNotification({
      open: true,
      message
    });
    setTimeout(() => setNotification({
      open: false,
      message: ''
    }), NOTIFICATION_HIDE_DELAY_MS);
  }, []);

  // ==============================
  // 3.イベントハンドラ
  // ==============================

  // ------------------------------
  // 3.1 共通UI操作ハンドラ
  // ------------------------------
  // 通知メッセージを閉じる（Notificationコンポーネントのクローズボタン用）
  const handleNotificationClose = useCallback(() => {
    setNotification({ open: false, message: '' });
  }, []);

  // ------------------------------
  // 3.2 地図位置情報ハンドラ
  // ------------------------------
  // 地図の中心位置から住所・最寄駅情報を取得し、stateを更新
  const updateMapLocation = useCallback((map) => {
    if (!map) return Promise.reject(new Error('Map not available'));

    const center = map.getCenter();

    return updateLocationInfo(center.lat, center.lng)
      .then(info => {
        // 住所と最寄駅の情報の更新
        setLocationInfo({
          address: info?.address ?? ADDRESS_NOT_FOUND,
          stations: info?.stations ?? [],
          center: { lat: center.lat, lng: center.lng }
        });
        return {
          ...info,
          center: { lat: center.lat, lng: center.lng }
        };
      })
      .catch(() => {
        return { address: ADDRESS_FETCH_FAILED, stations: [] };
      });
  }, []);

  // 地図移動完了時に呼ばれ、位置変更が閾値を超えた場合に住所情報と天気を更新
  // Map.jsxで地図のmoveendイベント発火時に渡される
  // mapState:地図コンポーネント（Map.jsx）から「今の地図の状態」を受け取るための一時的な値
  const handleMapMoveEnd = useCallback((mapState) => {

    if (mapState && mapState.center) {
      // 前の状態と比較して、閾値より大きく変わった場合だけ更新する
      // これにより、微小な移動やズームの誤差で無駄な再レンダリングやAPIリクエストを防ぐ
      const hasSignificantChange =
        // mapConfig:App.jsで現在保持している地図の状態（center,zoom,pitch,bearing,style,mode）
        // 初回は必ずtrueにする
        !mapConfig.center ||
        Math.abs(mapState.center.lat - mapConfig.center.lat) > POSITION_THRESHOLD ||
        Math.abs(mapState.center.lng - mapConfig.center.lng) > POSITION_THRESHOLD ||
        Math.abs(mapState.zoom - mapConfig.zoom) > ZOOM_THRESHOLD;

      // 上記チェックで変化が閾値を超えた場合のみ、地図状態（mapConfig）を更新
      if (hasSignificantChange) {
        // styleやmodeなどの既存プロパティを保持しつつ、位置関連のプロパティだけを更新
        setMapConfig(prev => ({
          ...prev,
          center: { ...mapState.center },
          zoom: mapState.zoom,
          pitch: mapState.pitch,
          bearing: mapState.bearing
        }));
        // ユーザー操作の移動時だけ再検索ボタンを出す。検索後の小さな移動は無視
        if (mapState.isUserTriggered) {
          setHasMapMoved(true);
        }

        // mapInstance.currentが存在する場合、現在の地図インスタンスから位置情報を取得・更新
        // これにより、住所や最寄駅などの情報も最新化される
        if (mapInstance.current) {
          updateMapLocation(mapInstance.current);
        }

        // 天気情報の取得
        fetchWeather(mapState.center.lat, mapState.center.lng)
          .then(weatherData => {
            setWeather(weatherData);
          })
          .catch(error => {
            setWeather(null);
          });
      }
    }
    // 意図的に依存配列を空にしている。極小変化で発火と再実行がループするのを防ぐため
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 地図の初期化完了時に呼ばれ、初期位置の住所と天気情報を取得・表示
  const handleMapInitialized = useCallback((map) => {
    if (!map) {
      return;
    }

    // 地図インスタンスを保存（他の処理で使用するため）
    mapInstance.current = map;
    setIsMapInitialized(true);

    // URLに座標があるか、またはキーワード検索の指定があるかをチェック
    const params = new URLSearchParams(window.location.search);
    const hasQueryParam = params.has(SEARCH_QUERY_PARAM);
    const hasMySpotParam = params.has(MYSPOT_PARAM);
    const hasHistoryParam = params.has(HISTORY_PARAM);
    const hasPoiParam = params.has(SEARCH_POI_PARAM);
    const hasPlacePath = (window.location.pathname || '').startsWith(`/${PLACE_PREFIX}/`);
    const hasUrlIntention = mapConfig.hasUrlCoords || hasQueryParam || hasPoiParam || hasPlacePath || hasMySpotParam || hasHistoryParam;

    if (hasUrlIntention) {
      // 住所情報取得
      updateLocationInfo(mapConfig.center.lat, mapConfig.center.lng)
        .then(info => {
          // 住所情報取得成功：stateに保存
          setLocationInfo({
            address: info?.address ?? ADDRESS_NOT_FOUND,
            stations: info?.stations ?? [],
            center: mapConfig.center
          });

          // 天気情報取得
          fetchWeather(mapConfig.center.lat, mapConfig.center.lng)
            .then(weatherData => {
              // 天気情報取得成功：stateに保存
              setWeather(weatherData);
            })
            .catch(() => {
              // 天気取得失敗：天気だけnullに
              setWeather(null);
            });
        })
        .catch(() => {
          // 住所取得失敗：住所を「取得できませんでした」に
          setLocationInfo({
            address: ADDRESS_FETCH_FAILED,
            stations: [],
            center: mapConfig.center
          });
          // 天気は取得しない
          setWeather(null);
        });

    } else {
      // URLに座標がない場合：まず現在地取得、できなければデフォルト位置
      getCurrentLocationCoords()
        .then(({ lat, lon }) => {
          // 現在地取得成功：地図の中心を現在地に移動
          setMapConfig(prev => ({ ...prev, center: { lat, lng: lon } }));

          // 地図インスタンスの中心も更新
          try {
            map.setCenter([lon, lat]);
          } catch (e) {
            // setCenter が失敗しても処理を続ける
          }

          // 現在地の住所・天気を取得
          updateMapLocation(map)
            .then(() => {
              // 住所取得成功：天気情報を取得
              fetchWeather(lat, lon)
                .then(data => {
                  // 天気情報取得成功：stateに保存
                  setWeather(data);
                })
                .catch(() => {
                  // 天気取得失敗：天気をnullに
                  setWeather(null);
                });
            })
            .catch(() => {
              // 住所取得失敗：天気もnullに
              setWeather(null);
            });
        })
        .catch(() => {
          // 現在地取得失敗：デフォルト位置の住所・天気を取得
          updateMapLocation(map)
            .then(info => {
              // 住所取得成功：天気情報を取得
              fetchWeather(info.center.lat, info.center.lng)
                .then(data => {
                  // 天気情報取得成功：stateに保存
                  setWeather(data);
                })
                .catch(() => {
                  // 天気取得失敗
                  setWeather(null);
                });
            })
            .catch(() => {
              // 住所取得失敗：天気もnullに
              setWeather(null);
            });
        });
    }
    // 意図的に依存配列を固定
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateMapLocation]);

  // 位置情報更新ハンドラ
  const handleLocationUpdate = useCallback((newInfo) => {
    setLocationInfo(prev => ({
      // 既存の情報を保持
      ...prev,
      // 渡された新しい情報で上書き
      ...newInfo
    }));
  }, []);

  // ------------------------------
  // 3.3 検索関連ハンドラ
  // ------------------------------

  // マップコード検索処理
  const handleMapcodeSearch = useCallback((rawCode) => {
    const code = String(rawCode || '').trim();
    if (!code) {
      showNotification(MAPCODE_EMPTY);
      return null;
    }

    try {
      // 形式{ lon, lat } (WGS84)
      const res = getLonLat(code);
      const lat = Number(res?.lat);
      const lng = Number(res?.lon);

      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        showNotification(MAPCODE_CONVERSION_ERROR);
        return null;
      }

      // 検索結果1件扱い
      // itemsは、lat/lonを期待しているのでlonに合わせる
      const item = {
        type: 'spot',
        name: `mapcode:${code}`,
        poi_name: `mapcode:${code}`,
        lat,
        lon: lng,
      };
      const newResult = {
        data: {
          items: [item],
          total: 1,
          query: item.name,
        },
        status: 'success',
      };

      // searchResultを更新することで、MapコンポーネントのuseEffectが反応
      setSearchResult(newResult);
      return newResult;
    } catch (e) {
      showNotification(MAPCODE_PARSE_ERROR);
      return null;
    }
  }, [showNotification]);

  // サジェスト結果が選択された時に、再検索せずに直接結果として設定する
  const handleSuggestionSelect = (item) => {
    if (!item) {
      return null;
    }

    // 検索結果の形式に整形してstateを更新
    const newResult = {
      data: {
        // サジェスト結果を配列にラップ
        items: [item],
        total: 1,
        query: item.name || item.poi_name || ''
      },
      status: 'success'
    };
    setSearchResult(newResult);

    // 検索キーワードも更新
    setSearchQuery(item.name || item.poi_name || '');

    // 検索結果パネルを開く
    setIsSearchResultOpen(true);
    // 折りたたみ状態が残っていても、新しい検索結果を表示時には必ず見えるようにする
    setIsSearchResultVisible(true);

    // 1件だけの結果なので「この辺りで再検索」は無効化
    setIsResearchHereEnabled(false);

    // 検索モーダルを閉じる
    setIsSearchModalOpen(false);
  };

  // 検索ボックスから検索する処理の入口であり、URL解析しての検索時もここを通る
  // キーワード検索を実行し、結果を表示するメインの検索フロー
  const handleSearch = async (
    searchKeyword,
    categoryCode = null,
    categoryType = null,
    center = null,
    isFromUrl = false,
    isPoiSearch = false
  ) => {

    // 共通：検索UI/状態の初期化
    // 検索結果の選択中インデックスをリセット
    setSelectedIndex(null);
    // カテゴリ選択解除
    // setSelectedCategory(null);

    // poi検索の時は検索ボックスに残さない
    if (!isPoiSearch) {
      // 検索ボックスに入力値をセット
      setSearchQuery(searchKeyword);
      // 検索ワードを保持（最後に検索したキーワードとして、再検索に使用）
      setLastSearchTerm(searchKeyword);
    }

    // はじめに、特殊処理のmapcode:を分岐
    const raw = String(searchKeyword || '');
    const m = raw.match(/^mapcode\s*:\s*(.+)$/i);
    if (m) {
      const code = (m[1] || '').trim();

      // マップコードの場合は再検索不要
      setIsResearchHereEnabled(false);
      // マップコードから座標を取得して移動
      handleMapcodeSearch(code);
      // 地図移動フラグをリセット
      setHasMapMoved(false);
      // マップコードでは検索結果パネルを開かない
      setIsSearchResultOpen(false);
      // 検索モーダルを閉じる
      setIsSearchModalOpen(false);
      return;
    }

    // ここから通常のキーワード検索処理
    // 再検索ボタンを有効に設定（無効状態のリセット）
    setIsResearchHereEnabled(true);

    // 検索オプションの準備（基本形と異なる検索のフラグを持つ）
    const searchOptions = {};
    // カテゴリタイプが'station'の場合は駅検索モードに
    if (categoryType === CATEGORY_TYPE_STATION || searchKeyword === CATEGORY_TYPE_STATION_JP) {
      searchOptions.isStationSearch = true;
    }
    // ?poi=xxx の検索の場合にtrue
    // 現時点では、取得処理の種類はデフォルトと駅の2通り。今後拡張する可能性も
    searchOptions.isPoiSearch = isPoiSearch;

    // 最新の中心座標を使う
    const searchCenter = center || mapConfig.center;

    // 検索APIで検索実行して結果を受け取る
    const result = await executeSearch(searchKeyword, searchCenter, searchOptions);

    // 検索結果をstateに保存（Mapコンポーネントでマーカー表示を更新）
    setSearchResult(result);

    const itemsLen = result?.data?.items?.length ?? 0;

    // isFromUrl（URLから直接検索）かつ結果が1件の場合は「この辺りで再検索」を出さない
    if (isFromUrl && itemsLen === 1) {
      setIsResearchHereEnabled(false);
    } else {
      setIsResearchHereEnabled(true);
    }

    // 検索結果0件の場合
    if (result?.data?.items?.length === 0) {
      // 0件が確定した時点で「このエリアで検索」は不要（移動してなくても出てしまうのを防ぐ）
      setIsResearchHereEnabled(false);
      setHasMapMoved(false);
      if (isPoiSearch) {
        // poi検索で結果0件だった場合はフラグを立ててメッセージ制御に使用
        setIsPoiSearchNoResults(true);
        // URLパラメータのpoiを削除
        removePoiParam();
      }

      // URLからの検索(/place/や、?poi=xxxなど)で結果が0件だった場合
      // URLに座標があればその座標を使用。なければまず現在位置、取得できなければデフォルト住所
      if (isFromUrl) {

        const routeData = parseRoute(window.location.pathname || '', { isMobileDevice });
        const urlCenter =
          routeData?.mapConfig?.hasUrlCoords && routeData?.mapConfig?.center
            ? routeData.mapConfig.center
            : null;

        // URLに座標あり
        if (urlCenter) {
          setMapConfig(
            prev => ({
              ...prev,
              center: { lat: urlCenter.lat, lng: urlCenter.lng }
            }));
          if (mapInstance.current) {
            mapInstance.current.setCenter([urlCenter.lng, urlCenter.lat]);
          }
        } else {

          getCurrentLocationCoords()
            .then(({ lat, lon }) => {
              // 現在地取得成功：地図の中心を現在地に移動
              setMapConfig(prev => ({ ...prev, center: { lat, lng: lon } }));
              if (mapInstance.current) {
                mapInstance.current.setCenter([lon, lat]);
              }
            })
            .catch(() => {
              // 現在地取得失敗：何もしない（地図はデフォルト位置）
            });
        }
      } else {
        // 通常の検索で結果が0件の場合は、パネル（SearchModal, SearchResultPanel）を閉じるのみ
        setIsSearchModalOpen(false);
        setIsSearchResultOpen(false);
      }
      return;
    }
    // 地図移動フラグをリセット
    setHasMapMoved(false);

    // 検索結果パネルを開く
    setIsSearchResultOpen(true);
    // 折りたたみ状態が残っていても、新しい検索結果を表示時には必ず見えるようにする
    setIsSearchResultVisible(true);

    // 検索モーダルを閉じる
    setIsSearchModalOpen(false);
  };

  //「この辺りで検索」ボタンクリック時に現在の地図中心で再検索
  const handleResearchHere = useCallback(() => {
    if (lastSearchTerm && mapInstance.current) {
      const center = mapInstance.current.getCenter();
      handleSearch(lastSearchTerm, null, null, center, false, false);

      // 地図移動フラグをリセット
      setHasMapMoved(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastSearchTerm]);

  // カテゴリ検索
  const handleCategorySelection = async (searchKeyword, categoryCode = null, categoryType = null) => {
    // 地図移動フラグをリセット
    setHasMapMoved(false);
    // 検索ボックスにワードをセット
    setSearchQuery(searchKeyword);
    // 検索結果の選択インデックスをリセット
    setSelectedIndex(null);
    // 「この辺で検索」ボタンを有効に
    setIsResearchHereEnabled(true);

    // 実際の検索を実行（上記のhandleSearchを呼ぶ）
    await handleSearch(searchKeyword, categoryCode, categoryType, null, false, false);

    // 選択カテゴリを記憶
    setSelectedCategory(searchKeyword);

  };

  // ------------------------------
  // 3.4 マップ表示制御ハンドラ
  // ------------------------------
  // ピッチ変更
  const handlePitchChange = useCallback((newPitch) => {
    setMapConfig(prev => ({
      ...prev,
      pitch: newPitch
    }));
  }, []); // 依存配列を空にして関数を固定

  // 方位変更
  const handleBearingChange = useCallback((newBearing) => {
    setMapConfig(prev => ({
      ...prev,
      bearing: newBearing
    }));
  }, []);

  // 地図スタイルの切り替え
  const handleStyleChange = useCallback((newStyle) => {
    const resolvedStyleUrl = config.api.map.styles(newStyle);
    const defaultStyleUrl = config.api.map.styles(DEFAULT_STYLE_NAME);
    let resolvedStyleName;
    if (resolvedStyleUrl === defaultStyleUrl) {
      resolvedStyleName = DEFAULT_STYLE_NAME;
    } else {
      resolvedStyleName = newStyle;
    }

    setMapConfig(prev => ({
      ...prev,
      style: resolvedStyleUrl || defaultStyleUrl,
      styleName: resolvedStyleName
    }));
  }, []);

  // 中心点インジケーターの表示/非表示を切り替え
  const handleToggleCenterIndicator = () => setIsCenterIndicatorEnabled(v => !v);

  // 3Dモードの切り替え
  const handle3dModeToggle = () => {
    setIs3dMode(prev => {
      const next = !prev;
      setMapConfig(cfg => ({
        ...cfg,
        pitch: next ? PITCH_FOR_3D_MODE : 0
      }));
      return next;
    });
  };

  // ------------------------------
  // 3.5 印刷関連ハンドラ
  // ------------------------------
  // 通常印刷モーダルを表示
  const handleNormalPrint = () => {
    setIsPrintMapNormalOpen(true);
    setIsShowNavi(false); // ナビ表示を閉じる
  };

  // 超印刷モーダルを表示
  const handleSuperPrint = () => {
    setIsPrintMapSuperOpen(true);
    // 超印刷を開いたときは...
    setIsShowNavi(false); // ナビ表示を閉じる
    setIsSearchResultOpen(false); // 検索結果パネルを閉じる
  };

  // ==============================
  // 4.キーボードショートカット関連
  // ==============================

  // 各ショートカットのアクションは別のサービスクラスで処理している

  // ==============================
  // 5.副作用（useEffect）
  // ==============================

  // ------------------------------
  // 5.1 URL連携
  // ------------------------------
  // 初回マウント時にURLを見て、必要なら検索を自動発火する
  useEffect(() => {

    // kntrでは検索しない（URL連動はuseUrlSync/moveend側でやる）
    if (isFromKntr) {
      return;
    }

    // 1. クエリパラメータをチェック
    const params = new URLSearchParams(window.location.search);
    const qParam = params.get(SEARCH_QUERY_PARAM);
    const poiParam = params.get(SEARCH_POI_PARAM);

    // 1.1 ?q=xxx の形式
    if (qParam) {
      let decodedKeyword = '';
      try {
        decodedKeyword = decodeURIComponent(qParam);
      } catch (e) {
        decodedKeyword = qParam;
      }

      // サニタイズ：
      decodedKeyword = sanitizeKeyword(decodedKeyword, MAX_KEYWORD_LENGTH);
      // 第5引数はURLから直接検索したことを示すフラグ
      // 第6引数はpoi検索であることを示すフラグ
      handleSearch(decodedKeyword, null, null, null, true, false);
      return; // ?q= がある場合は以降のチェックをスキップ
    }

    // 1.2 ?poi=xxx の形式、こちらはサーチAPIで検索を行う
    if (poiParam) {
      let decodedKeyword = '';
      try {
        decodedKeyword = decodeURIComponent(poiParam);
      } catch (e) {
        decodedKeyword = poiParam;
      }
      // サニタイズ：
      decodedKeyword = sanitizeKeyword(decodedKeyword, MAX_KEYWORD_LENGTH);

      // poiが :@139_7498053-35_6427937 の場合はmyspotや履歴のURLであるため検索せず、URLの緯度経度の場所に落ち着かせる
      // 詳しくは→ https://ghe.aws01.mapion.co.jp/mapiondev/MapionApi/wiki/%E3%83%9E%E3%82%A4%E3%82%B9%E3%83%9D%E3%83%83%E3%83%88API
      // 位置情報スポット":@<lng>-<lat>" 例：":@139_6616072-35_5806436" lng、latはサーバ側で小数点以下7桁で丸めます
      if (/^:@\d+_\d+-\d+_\d+$/.test(decodedKeyword)) {
        return;
      }

      // 第5引数はURLから直接検索したことを示すフラグ
      // 第6引数はpoi検索であることを示すフラグ
      handleSearch(decodedKeyword, null, null, null, true, true);
      return; // ?poi= がある場合は以降のチェックをスキップ
    }

    // 2. /place/keyword の形式をチェック
    const routeData = parseRoute(window.location.pathname || '', {
      isMobileDevice,
      search: window.location.search,
    });

    if (routeData.isPlaceSearch && routeData.keyword) {

      let decodedKeyword = '';
      try {
        decodedKeyword = decodeURIComponent(routeData.keyword);
      } catch (e) {
        decodedKeyword = routeData.keyword;
      }
      decodedKeyword = sanitizeKeyword(decodedKeyword, MAX_KEYWORD_LENGTH);
      // 第5引数はURLから直接検索したことを示すフラグ
      handleSearch(decodedKeyword, null, null, null, true, false);
    }
    // 初回のみ実行
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getCurrentSearchQuery = useCallback(() => searchQuery, [searchQuery]);
  useUrlSync(
    mapInstance.current,
    handleMapMoveEnd,
    mapConfig.styleName,
    getCurrentSearchQuery
  );

  // ------------------------------
  // 5.2 検索結果監視
  // ------------------------------
  // 検索結果0件の場合にメッセージ
  useEffect(() => {
    if (
      searchResult &&
      Array.isArray(searchResult.data?.items) &&
      searchResult.data.items.length === 0 &&
      // POI検索で結果0件だった場合は無視
      !isPoiSearchNoResults
    ) {
      showNotification(SEARCH_NO_RESULTS);
    }
  }, [searchResult, isPoiSearchNoResults, showNotification]);
  // ------------------------------
  // 5.3 キーボードショートカット監視
  // ------------------------------
  useEffect(() => {
    const handleKeyDown = (event) => {

      // 「Ctrl + /」でキーボードショートカット情報を開閉
      // ※環境によりevent.keyが"/"にならないことがあるので、codeも見る
      const isCtrlSlash = event.ctrlKey && (event.key === '/' || event.code === KEY_NAME_SLASH);
      if (isCtrlSlash) {
        event.preventDefault();
        setIsKeyboardInfoOpen(v => !v);
        return;
      }

      // 「Esc」で閉じる（開いている時だけ）
      if (event.key === KEY_NAME_ESCAPE && isKeyboardInfoOpen) {
        event.preventDefault();
        setIsKeyboardInfoOpen(false);
        return;
      }

      // 「,」でメニューを開く
      if (event.key === ',' && !isKeyboardInfoOpen) {
        event.preventDefault();
        setIsMenuOpen(true);
        return;
      }

      // 「Ctrl + Shift + キー」の組み合わせをチェック
      if (event.ctrlKey && event.shiftKey) {
        switch (event.key.toLowerCase()) {

          // F:フルスクリーン切替（フルスクリーンボタンと同様）
          case 'f':
            // ブラウザのデフォルト動作（例：検索窓フォーカスなど）を防止
            event.preventDefault();
            toggleMapContentFullScreen();
            break;

          // L:現在地表示（現在地ボタンと同様）
          case 'l':
            event.preventDefault();
            getCurrentLocation(mapInstance.current, setCurrentLocationButtonState, setHasMapMoved);
            break;

          // N:北向きリセット（コンパスボタンと同様）
          case 'n':
            event.preventDefault();
            resetMapToNorth(mapInstance.current, is3dMode, setIs3dMode);
            break;

          // R:デフォルト設定に戻す
          case 'r':
            event.preventDefault();
            resetMapToDefault(mapInstance.current, is3dMode, setIs3dMode, handleStyleChange);
            break;

          // S:スナップショット作成（スナップショットボタンと同様）
          case 's':
            event.preventDefault();
            takeMapSnapshot(mapInstance.current);
            break;

          // 1:デフォルト地図に戻す
          case '1':
            event.preventDefault();
            // デフォルトスタイルへ切り替え
            handleStyleChange(DEFAULT_STYLE_NAME);
            break;

          // 2:境界線マップ
          case '2':
            event.preventDefault();
            // 境界線マップへ切り替え
            handleStyleChange(STYLES.boundary);
            break;

          // 3:通り名マップ
          case '3':
            event.preventDefault();
            // 通り名マップへ切り替え
            handleStyleChange(STYLES.street);
            break;

          // 4:地形図
          case '4':
            event.preventDefault();
            // 地形図へ切り替え
            handleStyleChange(STYLES.terrain);
            break;

          // 5:ひらがなマップ
          case '5':
            event.preventDefault();
            // ひらがなマップへ切り替え
            handleStyleChange(STYLES.kana);
            break;

          default:
            break;
        }
      }
    };

    // イベントリスナーを登録
    window.addEventListener('keydown', handleKeyDown);

    // クリーンアップ関数
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [is3dMode, handleStyleChange, isKeyboardInfoOpen]);

  // ==============================
  // 6.レンダリング
  // ==============================

  // 機能が限られた国盗り専用マップ
  if (isFromKntr) {
    return (
      <div className="App">
        <div className="map-container">
          <div className="map-content">
            <div className="map-area">
              <KntrMap
                {...mapConfig}
                // スタイルはデフォルトで固定
                style={config.api.map.styles(DEFAULT_STYLE_NAME)}
                center={mapConfig?.center}
                zoom={mapConfig?.zoom}
                onMapInitialized={handleMapInitialized}
                onMoveEnd={handleMapMoveEnd}
                // 中心マークはオフにはならない
                isCenterIndicatorEnabled={true}
                // 現在地ボタン用
                currentLocationButtonState={currentLocationButtonState}
                setCurrentLocationButtonState={setCurrentLocationButtonState}
                // この先の国盗り用画面を構成する上でtrueを渡すもの
                isFromKntr={isFromKntr}
              />

              {/* 国盗り専用マップでの位置情報コンポーネント */}
              <div className="kntr-location-info">
                <KntrLocationInfo
                  // 住所情報をもつ
                  locationInfo={locationInfo}
                  // 位置情報が更新されたときのコールバック
                  onLocationUpdate={handleLocationUpdate}
                />
              </div>

            </div>
          </div>
        </div>
      </div>
    );
  }

  // 以降、通常マップ
  return (
    <div className="App">
      <div className="map-container">
        <div className="map-content">
          <div className="map-area">

            {/* 通知コンポーネント */}
            <Notification
              open={notification.open}
              message={notification.message}
              onClose={handleNotificationClose}
            />

            {/* 再検索ボタンコンポーネント */}
            {hasMapMoved && lastSearchTerm && isResearchHereEnabled && (
              <ResearchHere
                handleResearchHere={handleResearchHere}
                setIsResearchHereEnabled={setIsResearchHereEnabled}
              />
            )}

            {/* 地図表示エリア */}
            <Map
              {...mapConfig}
              // 地図初期化時のコールバック
              onMapInitialized={handleMapInitialized}
              // 地図スタイル変更時のコールバック
              onStyleChange={handleStyleChange}
              // 地図移動終了時のコールバック
              onMoveEnd={handleMapMoveEnd}

              // レイヤーポップアップの表示状態
              isLayerPopupOpen={isLayerPopupOpen}
              // レイヤーポップアップの表示状態を更新
              setIsLayerPopupOpen={setIsLayerPopupOpen}

              // 印刷ポップアップの表示状態 （右クリック時のメニューにあるため）
              isPrintPopupOpen={isPrintPopupOpen}
              // 印刷ポップアップの表示状態を更新
              setIsPrintPopupOpen={setIsPrintPopupOpen}

              // 通常印刷ボタン押下時の処理
              handleNormalPrint={handleNormalPrint}
              // 超印刷ボタン押下時の処理
              handleSuperPrint={handleSuperPrint}

              // 中心点インジケーターの表示状態
              isCenterIndicatorEnabled={isCenterIndicatorEnabled}
              // 中心点インジケーターのON/OFF切替
              handleToggleCenterIndicator={handleToggleCenterIndicator}

              // 現在地ボタンの状態
              currentLocationButtonState={currentLocationButtonState}
              // 現在地ボタンの状態を更新
              setCurrentLocationButtonState={setCurrentLocationButtonState}

              // ピッチ変更時のコールバック
              handlePitchChange={handlePitchChange}
              // 方位変更時のコールバック
              handleBearingChange={handleBearingChange}

              // 3DモードのON/OFF状態
              is3dMode={is3dMode}
              // 3DモードのON/OFF切替
              setIs3dMode={setIs3dMode}
              // 3Dモード切替時の処理
              on3dModeToggle={handle3dModeToggle}

              // メニューボタン押下時の処理
              onMenuOpen={() => setIsMenuOpen(true)}

              // --- SearchコンポーネントもMap経由 ---
              // 検索モーダルの表示状態
              isSearchModalOpen={isSearchModalOpen}
              // 検索モーダルを開く処理
              onOpenSearchModal={onOpenSearchModal}
              // 検索モーダルを閉じる処理
              onCloseSearchModal={onCloseSearchModal}
              // カテゴリ検索モーダルの表示状態
              isCategorySearchModalOpen={isCategorySearchModalOpen}
              // カテゴリ検索モーダルを開く処理
              onOpenCategorySearchModal={onOpenCategorySearchModal}
              // カテゴリ検索モーダルを閉じる処理
              onCloseCategorySearchModal={onCloseCategorySearchModal}

              // 検索結果パネルの表示状態
              isSearchResultOpen={isSearchResultOpen}
              // 検索結果パネルの表示状態を更新
              setIsSearchResultOpen={setIsSearchResultOpen}
              // 検索結果パネルの一時表示状態
              isSearchResultVisible={isSearchResultVisible}
              // 検索結果パネルの一時表示状態を更新
              setIsSearchResultVisible={setIsSearchResultVisible}

              // 検索結果データ
              searchResult={searchResult}
              // 検索結果データを更新
              setSearchResult={setSearchResult}
              // 検索クエリ
              searchQuery={searchQuery}
              // 検索クエリを更新
              setSearchQuery={setSearchQuery}
              // 検索結果の選択中インデックス
              selectedIndex={selectedIndex}
              // 選択中インデックスを更新
              setSelectedIndex={setSelectedIndex}
              // 検索実行のコールバック
              handleSearch={handleSearch}
              // サジェスト選択時のコールバック
              handleSuggestionSelect={handleSuggestionSelect}
              // カテゴリ検索のコールバック
              handleCategorySelection={handleCategorySelection}
              // 選択中カテゴリ
              selectedCategory={selectedCategory}
              // 選択中カテゴリを更新
              setSelectedCategory={setSelectedCategory}
              // 通知表示のコールバック
              showNotification={showNotification}
              // 最後に検索したワード設定
              setLastSearchTerm={setLastSearchTerm}
              // 地図移動フラグ設定
              setHasMapMoved={setHasMapMoved}
              // 検索結果のマウスオーバー中のインデックス
              hoveredIndex={hoveredIndex}
              // マウスオーバー中インデックスを更新
              setHoveredIndex={setHoveredIndex}
              // 「この辺で検索」ボタン状態を更新
              setIsResearchHereEnabled={setIsResearchHereEnabled}

              // ルート検索表示状態
              isShowNavi={isShowNavi}
              // ルート検索表示状態を更新するコールバック
              setIsShowNavi={setIsShowNavi}
              // ルート検索モデル
              naviSearch={naviSearch}
              // ルート検索モデルを更新するコールバック
              setNaviSearch={setNaviSearch}

              // 長押しポップアップの表示状態
              isLongPressPopupOpen={isLongPressPopupOpen}
              // 長押しポップアップの表示状態を更新するコールバック
              setIsLongPressPopupOpen={setIsLongPressPopupOpen}
              // 長押し位置情報
              longPressPosition={longPressPosition}
              // 長押し位置情報を更新するコールバック
              setLongPressPosition={setLongPressPosition}

              // モバイル・タブレット判定
              isTablet={isTablet}
              isMobileDevice={isMobileDevice}

              // 超印刷モーダルの表示状態を更新
              setIsPrintMapSuperOpen={setIsPrintMapSuperOpen}
            />
          </div>

          {/* 住所と最寄り駅（map-areaの中・Mapの外） */}
          {isMapInitialized && (
            <div className="location-info">
              <LocationInfo
                // 住所と最寄駅などの情報オブジェクト
                locationInfo={locationInfo}
                // 位置情報が更新されたときのコールバック
                onLocationUpdate={handleLocationUpdate}
                // 天気情報オブジェクト
                weather={weather}
                // （モバイルにて）地図の中心で長押しポップアップを開くための関数
                openLongPressAtMapCenter={openLongPressAtMapCenter}
                // 検索結果パネルの表示状態
                isSearchResultOpen={isSearchResultOpen}
                // 検索結果パネルの一時表示状態
                isSearchResultVisible={isSearchResultVisible}
                // 検索結果パネルの一時表示状態を更新
                setIsSearchResultVisible={setIsSearchResultVisible}
                // モバイル・タブレット判定
                isTablet={isTablet}
              />
            </div>
          )}

          {/* デスクトップ広告コンポーネント：画面での操作や他のパネルとの表示制御のため、map-container内に配置 */}
          {!isMobileDevice && (
            <DesktopAd
              isSearchResultOpen={isSearchResultOpen}
              isSearchModalOpen={isSearchModalOpen}
              isCategorySearchModalOpen={isCategorySearchModalOpen}
              searchResult={searchResult}
              isShowNavi={isShowNavi}
            />
          )}

          {/* アプリで見るコンポーネント：他のパネルとの表示制御のため、map-container内に配置 */}
          {isMobileDevice && (
            <ViewInApp
              // 地図の中心座標とズームレベルとスタイルを使用
              mapConfig={mapConfig}
            />
          )}

          <Menu
            // メニューの開閉状態
            isOpen={isMenuOpen}
            // メニューを閉じる処理
            onClose={() => setIsMenuOpen(false)}
            // モバイル・タブレット判定
            isMobileDevice={isMobileDevice}
          />

          <LayerPopup
            isLayerPopupOpen={isLayerPopupOpen}
            // ポップアップを閉じる際の処理
            onCloseLayerPopup={() => setIsLayerPopupOpen(false)}
            onStyleChange={handleStyleChange}
            currentStyleName={mapConfig?.styleName}
          />

          {/* 状態管理一元化により、ポップアップ表示非表示の状態もApp.jsで保持。そのため印刷ポップアップについてもここに記述 */}
          <PrintPopup
            // 印刷ポップアップの表示状態 （右クリック時のメニューにあるため）
            isPrintPopupOpen={isPrintPopupOpen}
            // 印刷ポップアップの表示状態を更新
            setIsPrintPopupOpen={setIsPrintPopupOpen}
            // 印刷ポップアップを閉じる処理
            onClosePrintPopup={() => setIsPrintPopupOpen(false)}

            // 通常印刷の処理
            onNormalPrint={handleNormalPrint}
            // 超印刷の処理
            onSuperPrint={handleSuperPrint}
            // 通常印刷モーダルの表示状態
            isPrintMapNormalOpen={isPrintMapNormalOpen}
            // 通常印刷モーダルの表示状態を更新
            setIsPrintMapNormalOpen={setIsPrintMapNormalOpen}
            // 超印刷モーダルの表示状態
            isPrintMapSuperOpen={isPrintMapSuperOpen}
            // 超印刷モーダルの表示状態を更新
            setIsPrintMapSuperOpen={setIsPrintMapSuperOpen}
          />

          {/* PrintMapNormalモーダルを表示 */}
          <PrintMapNormal
            isPrintMapNormalOpen={isPrintMapNormalOpen}
            setIsPrintMapNormalOpen={setIsPrintMapNormalOpen}
            // 地図の中心座標とズームレベルとスタイルを使用
            mapConfig={mapConfig}
          />

          {/* PrintMapSuperモーダルを表示 */}
          <PrintMapSuper
            isPrintMapSuperOpen={isPrintMapSuperOpen}
            setIsPrintMapSuperOpen={setIsPrintMapSuperOpen}
            // 地図の中心座標とズームレベルとスタイルを使用
            mapConfig={mapConfig}
            map={mapInstance.current}
            isOtherPanelOpen={isMenuOpen || isSearchModalOpen || isCategorySearchModalOpen || isPrintMapNormalOpen}
          />

          {/* キーボードショートカット情報 */}
          <KeyboardInfo
            isKeyboardInfoOpen={isKeyboardInfoOpen}
            onCloseKeyboardInfo={() => setIsKeyboardInfoOpen(false)}
          />

        </div>

        {/* モバイル（タブレット含む）広告コンポーネント：画面下部固定表示 */}
        {isMobileDevice && (
          <MobileAd />
        )}

      </div>
    </div >
  );
}

export default App;
